import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../api/axios";
import Select from "react-select";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../components/LoadingOverlay";
import Swal from "sweetalert2";

const RequestDelivery = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // =====================================================
  // VARIANTES
  // =====================================================

  const [variants, setVariants] = useState({});

  // Variante seleccionada temporalmente
  const [currentVariant, setCurrentVariant] = useState({});

  // Cantidad temporal
  const [currentQuantity, setCurrentQuantity] = useState({});

  // Variantes agregadas a entregar
  const [delivery, setDelivery] = useState({});

  // Motivos de entrega incompleta
  const [deliveryReasons, setDeliveryReasons] = useState({});

  // Insumos adicionales entregados
  const [products, setProducts] = useState([]);
  const [additionalVariants, setAdditionalVariants] = useState([]);
  const [additionalProduct, setAdditionalProduct] = useState(null);
  const [additionalVariant, setAdditionalVariant] = useState(null);
  const [additionalQuantity, setAdditionalQuantity] = useState("");
  const [additionalDelivery, setAdditionalDelivery] = useState([]);

  // =====================================================
  // FIRMAS
  // =====================================================

  const [warehouseSignature, setWarehouseSignature] = useState("");
  const [clientSignature, setClientSignature] = useState("");
  const [receiverName, setReceiverName] = useState("");
  const [warehouseSignatureDrawn, setWarehouseSignatureDrawn] = useState(false);
  const [clientSignatureDrawn, setClientSignatureDrawn] = useState(false);

  const warehouseCanvasRef = useRef(null);
  const clientCanvasRef = useRef(null);

  const [isDrawingWarehouse, setIsDrawingWarehouse] = useState(false);
  const [isDrawingClient, setIsDrawingClient] = useState(false);

  // =====================================================
  // CARGAR SOLICITUD
  // =====================================================

  useEffect(() => {
    fetchRequest();
  }, []);

  const fetchRequest = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/inventory-requests/${id}`);

      setRequest(res.data);

      await loadVariants(res.data.request_details);
      const productsRes = await api.get("/products");
      setProducts(productsRes.data);
    } catch (error) {
      console.error(error);

      toast.error("Error cargando solicitud");
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // CARGAR VARIANTES
  // =====================================================

  const loadVariants = async (details) => {
    let data = {};

    for (const detail of details) {
      try {
        const res = await api.get(
          `/products/${detail.product_id}/available-variants`,
        );

        data[detail.product_id] = res.data;
      } catch (error) {
        console.error(error);

        toast.error("Error cargando variantes");
      }
    }

    setVariants(data);
  };

  // =====================================================
  // MOTIVOS
  // =====================================================

  const deliveryReasonOptions = [
    {
      value: "No hay disponibilidad",
      label: "No hay disponibilidad en bodega",
    },
    {
      value: "El area tiene suficiente",
      label: "El área aún tiene suficiente inventario",
    },
    {
      value: "Disponibilidad insuficiente",
      label: "Disponibilidad insuficiente en bodega",
    },
  ];

  // =====================================================
  // TOTAL ENTREGADO
  // =====================================================

  const getTotalDelivered = (detailId) => {
    if (deliveryReasons[detailId] === "No hay disponibilidad") return 0;

    return (
      delivery[detailId]?.reduce(
        (sum, item) => sum + Number(item.quantity),
        0,
      ) || 0
    );
  };

  const loadAdditionalVariants = async (productId) => {
    if (!productId) {
      setAdditionalVariants([]);
      return;
    }

    try {
      const res = await api.get(`/products/${productId}/available-variants`);
      setAdditionalVariants(res.data);
    } catch (error) {
      console.error(error);
      toast.error("Error cargando variantes del insumo adicional");
    }
  };

  const addAdditionalVariant = () => {
    const quantity = Number(additionalQuantity);

    if (!additionalProduct || !additionalVariant) {
      toast.error("Seleccione el insumo y la variante");
      return;
    }

    if (!Number.isInteger(quantity) || quantity <= 0) {
      toast.error("Ingrese una cantidad válida");
      return;
    }

    const variant = additionalVariants.find(
      (item) => Number(item.id) === Number(additionalVariant),
    );
    const stock = Number(variant?.inventories?.[0]?.current_quantity || 0);
    const alreadyAdded = additionalDelivery
      .filter((item) => Number(item.variant_id) === Number(additionalVariant))
      .reduce((sum, item) => sum + item.quantity, 0);

    if (!variant || quantity + alreadyAdded > stock) {
      toast.warning(`Solo hay ${Math.max(0, stock - alreadyAdded)} unidades disponibles en este lote.`);
      return;
    }

    setAdditionalDelivery((prev) => [
      ...prev,
      {
        product_id: Number(additionalProduct),
        product_name: products.find((item) => Number(item.id) === Number(additionalProduct))?.name || "-",
        variant_id: variant.id,
        quantity,
        batch_number: variant.batch_number,
        invima_code: variant.invima_code || "-",
        brand: variant.brand?.name || "-",
        presentation: variant.presentation?.name || "-",
      },
    ]);
    setAdditionalProduct(null);
    setAdditionalVariant(null);
    setAdditionalVariants([]);
    setAdditionalQuantity("");
  };

  // =====================================================
  // AGREGAR VARIANTE
  // =====================================================

  const addVariant = (detail) => {
  const variantId = currentVariant[detail.id];
  const rawQuantity = currentQuantity[detail.id];

  if (deliveryReasons[detail.id] === "No hay disponibilidad") {
    toast.info("Este producto se registrará con cantidad 0 por el motivo seleccionado.");
    return;
  }

  // =====================================================
  // VALIDAR CANTIDAD
  // =====================================================

  if (
    rawQuantity === "" ||
    rawQuantity === undefined ||
    rawQuantity === null
  ) {
    toast.error("Ingrese una cantidad válida");
    return;
  }

  const quantity = Number(rawQuantity);

  if (Number.isNaN(quantity) || quantity < 0) {
    toast.error("Ingrese una cantidad válida");
    return;
  }

  // =====================================================
  // CASO ESPECIAL: CANTIDAD 0
  //
  // NO necesita variante.
  // Solo necesita motivo.
  // =====================================================

  if (quantity === 0) {
    if (!deliveryReasons[detail.id]) {
      toast.error(
        "Seleccione el motivo por el cual no se entrega el producto.",
      );
      return;
    }

    // -----------------------------------------------
    // REGISTRAR PRODUCTO CON CANTIDAD 0
    // -----------------------------------------------

    setDelivery((prev) => ({
      ...prev,
      [detail.id]: [
        // Quitamos cualquier fila anterior de cantidad 0
        ...(prev[detail.id] || []).filter(
          (item) => Number(item.quantity) > 0,
        ),

        {
          variant_id: null,
          quantity: 0,
          stock: 0,
          batch_number: "-",
          invima_code: "-",
          expiration_date: "-",
          brand: "-",
        },
      ],
    }));

    // -----------------------------------------------
    // LIMPIAR FORMULARIO
    // -----------------------------------------------

    setCurrentVariant((prev) => ({
      ...prev,
      [detail.id]: null,
    }));

    setCurrentQuantity((prev) => ({
      ...prev,
      [detail.id]: "",
    }));

    return;
  }

  // =====================================================
  // DESDE AQUÍ quantity > 0
  //
  // Para entregar unidades SÍ necesitamos variante.
  // =====================================================

  if (!variantId) {
    toast.error("Seleccione una variante");
    return;
  }

  // =====================================================
  // BUSCAR VARIANTE
  // =====================================================

  const variant = variants[detail.product_id]?.find(
    (v) => Number(v.id) === Number(variantId),
  );

  if (!variant) {
    toast.error("La variante seleccionada no existe.");
    return;
  }

  // =====================================================
  // STOCK DEL LOTE
  // =====================================================

  const stock = Number(
    variant.inventories?.[0]?.current_quantity || 0,
  );

  // =====================================================
  // CANTIDAD YA AGREGADA DEL LOTE
  // =====================================================

  const alreadyAdded =
    delivery[detail.id]
      ?.filter(
        (item) =>
          Number(item.variant_id) === Number(variant.id),
      )
      .reduce(
        (sum, item) => sum + Number(item.quantity),
        0,
      ) || 0;

  const available = stock - alreadyAdded;

  // =====================================================
  // VALIDAR STOCK
  // =====================================================

  if (available <= 0) {
    toast.warning(
      "Ese lote ya no tiene unidades disponibles.",
    );
    return;
  }

  if (quantity > available) {
    toast.warning(
      `Solo hay ${available} unidades disponibles en este lote.`,
    );
    return;
  }

  // =====================================================
  // TOTAL ENTREGADO
  // =====================================================

  const currentTotal = getTotalDelivered(detail.id);

  const newTotal = currentTotal + quantity;

  // =====================================================
  // NO SUPERAR LO SOLICITADO
  // =====================================================

  if (
    newTotal >
    Number(detail.requested_quantity)
  ) {
    toast.warning(
      `No puede entregar más de las ${detail.requested_quantity} unidades solicitadas.`,
    );
    return;
  }

  // =====================================================
  // SI QUEDA INCOMPLETA, EXIGIR MOTIVO
  // =====================================================

  if (
    newTotal <
    Number(detail.requested_quantity)
  ) {
    if (!deliveryReasons[detail.id]) {
      toast.error(
        "Seleccione el motivo por el cual no se entrega la cantidad completa.",
      );
      return;
    }
  }

  // =====================================================
  // CREAR ITEM
  // =====================================================

  const newItem = {
    variant_id: variant.id,
    quantity,
    stock,
    batch_number: variant.batch_number,
    invima_code: variant.invima_code,
    expiration_date: variant.expiration_date,
    brand: variant.brand?.name || "-",
  };

  // =====================================================
  // AGREGAR A DELIVERY
  // =====================================================

  setDelivery((prev) => {
    const currentItems = prev[detail.id] || [];

    const existingIndex = currentItems.findIndex(
      (item) =>
        Number(item.variant_id) ===
        Number(variant.id),
    );

    if (existingIndex >= 0) {
      const currentItem =
        currentItems[existingIndex];

      const newQuantity =
        Number(currentItem.quantity) + quantity;

      if (newQuantity > stock) {
        toast.warning(
          `Solo hay ${stock} unidades disponibles en este lote.`,
        );

        return prev;
      }

      const updatedItems = currentItems.map(
        (item, index) =>
          index === existingIndex
            ? {
                ...item,
                quantity: newQuantity,
              }
            : item,
      );

      return {
        ...prev,
        [detail.id]: updatedItems,
      };
    }

    return {
      ...prev,
      [detail.id]: [
        ...currentItems,
        newItem,
      ],
    };
  });

  // =====================================================
  // LIMPIAR FORMULARIO
  // =====================================================

  setCurrentVariant((prev) => ({
    ...prev,
    [detail.id]: null,
  }));

  setCurrentQuantity((prev) => ({
    ...prev,
    [detail.id]: "",
  }));
};
  // =====================================================
  // ELIMINAR VARIANTE
  // =====================================================

  const removeVariant = (detailId, index) => {
    setDelivery((prev) => ({
      ...prev,
      [detailId]: prev[detailId].filter((_, i) => i !== index),
    }));
  };

  // =====================================================
  // FIRMAS
  // =====================================================

  const startDrawing = (e, canvasRef, setDrawing, setSignatureDrawn) => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * (canvas.width / rect.width);
    const y = (e.clientY - rect.top) * (canvas.height / rect.height);

    e.currentTarget.setPointerCapture(e.pointerId);

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000000";
    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(x, y, 1.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(x, y);

    setDrawing(true);
    setSignatureDrawn(true);
  };

  const drawSignature = (e, canvasRef, isDrawing, setSignatureDrawn) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;

    if (!canvas) return;

    setSignatureDrawn(true);

    const ctx = canvas.getContext("2d");

    const rect = canvas.getBoundingClientRect();

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    const x = (e.clientX - rect.left) * scaleX;
    const y = (e.clientY - rect.top) * scaleY;

    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#000000";

    ctx.lineTo(x, y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const stopDrawing = (canvasRef, setDrawing, setSignature) => {
    if (!canvasRef.current) return;

    setDrawing(false);

    const canvas = canvasRef.current;
    setSignature(canvas.toDataURL("image/png"));
  };

  const clearSignature = (canvasRef, setSignature, setSignatureDrawn) => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    setSignature("");
    setSignatureDrawn(false);
  };

  // =====================================================
  // GUARDAR ENTREGA
  // =====================================================
  const saveDelivery = async () => {
    try {
      if (!warehouseSignatureDrawn) {
        toast.error("Debe registrar la firma del bodeguero.");
        return;
      }

      if (!clientSignatureDrawn) {
        toast.error("Debe registrar la firma de quien recibe.");
        return;
      }

      if (!receiverName.trim()) {
        toast.error("Debe ingresar el nombre de la persona que recibe.");
        return;
      }

      const data = {
        request_id: id,

        details: [
          ...request.request_details.flatMap((detail) => {
          const items = delivery[detail.id] || [];

          // Si no se entrega nada de este producto
          if (items.length === 0) {
            return [
              {
                request_detail_id: Number(detail.id),
                variant_id: null,
                quantity: 0,
                delivery_reason: deliveryReasons[detail.id] || null,
              },
            ];
          }

          // Si tiene una o varias variantes/lotes
          return items.map((item) => ({
            request_detail_id: Number(detail.id),
            variant_id: item.variant_id,
            quantity: Number(item.quantity),
            delivery_reason:
              getTotalDelivered(detail.id) < Number(detail.requested_quantity)
                ? deliveryReasons[detail.id] || null
                : null,
          }));
          }),
          ...additionalDelivery.map((item) => ({
            request_detail_id: null,
            variant_id: item.variant_id,
            quantity: item.quantity,
            delivery_reason: null,
          })),
        ],
        delivery_signature: warehouseSignature,
        receiver_signature: clientSignature,
        receiver_name: receiverName.trim(),
      };

      await api.post("/request-delivery", data);

      setShowConfirmModal(false);

      Swal.fire({
        icon: "success",
        title: "Entrega registrada",
        text: "La entrega fue registrada correctamente.",
        timer: 2000,
        showConfirmButton: false,
      }).then(() => {
        navigate(`/requests/${id}`);
      });
    } catch (error) {
      console.error(error);

      toast.error(
        error.response?.data?.message || "Error registrando la entrega.",
      );
    }
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div className="bg-white p-6 rounded-xl shadow-md">
      <LoadingOverlay loading={loading} />

      {request && (
        <>
          {/* HEADER */}

          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              Preparar entrega
            </h2>

            <button
              onClick={() => navigate(`/requests/${id}`)}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft size={18} />
              Volver
            </button>
          </div>

          {/* INFORMACIÓN */}

          <div className="mb-6 bg-gray-50 p-6 rounded-lg shadow-inner grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">
                N° Solicitud
              </label>

              <input
                type="text"
                value={request.request_number}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 w-full"
              />
            </div>

            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">
                Área solicitante
              </label>

              <input
                type="text"
                value={request.client?.name || ""}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 w-full"
              />
            </div>

            <div>
              <label className="text-sm font-bold text-gray-700 mb-1 block">
                Usuario solicitante
              </label>

              <input
                type="text"
                value={request.user?.name || ""}
                readOnly
                className="border rounded px-3 py-2 bg-gray-100 w-full"
              />
            </div>
          </div>

          {/* PRODUCTOS */}

          {request.request_details.map((detail) => {
            const sortedVariants = [
              ...(variants[detail.product_id] || []),
            ].sort(
              (a, b) =>
                new Date(a.expiration_date) - new Date(b.expiration_date),
            );

            return (
              <div key={detail.id} className="border rounded-xl p-5 mb-6">
                <h3 className="text-xl font-bold mb-4">
                  {detail.product?.name}
                </h3>

                <p className="text-gray-600 mb-4">
                  Presentación solicitada: {detail.presentation?.name || "-"}
                </p>

                {/* RESUMEN */}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                  <div>
                    <span className="text-gray-500">Stock al solicitar</span>

                    <p className="font-bold">
                      {Number(detail.stock_at_request ?? 0)}
                    </p>
                  </div>

                  <div>
                    <span className="text-gray-500">Solicitado</span>

                    <p className="font-bold">{detail.requested_quantity}</p>
                  </div>

                  <div>
                    <span className="text-gray-500">Entregando</span>

                    <p className="font-bold text-blue-600">
                      {getTotalDelivered(detail.id)}
                    </p>
                  </div>

                  <div>
                    <span className="text-gray-500">Pendiente</span>

                    <p className="font-bold">
                      {Math.max(
                        0,
                        Number(detail.requested_quantity) -
                          getTotalDelivered(detail.id),
                      )}
                    </p>
                  </div>
                </div>

                {/* FORMULARIO */}

                <div className="grid grid-cols-12 gap-4 items-start">
                  {/* LOTE */}

                  <div className="col-span-12 lg:col-span-6">
                    <label className="font-semibold block mb-2">
                      Variante / Lote
                    </label>

                    <Select
                      options={sortedVariants.map((v) => ({
                        value: v.id,
                        label:
                          `Presentación: ${v.presentation?.name || "-"} | ` +
                          `Lote: ${v.batch_number} | ` +
                          `Marca: ${v.brand?.name || "-"} | ` +
                          `Vence: ${v.expiration_date} | ` +
                          `Stock: ${v.inventories?.[0]?.current_quantity || 0}`,
                      }))}
                      value={
                        sortedVariants
                          .map((v) => ({
                            value: v.id,
                            label:
                              `Presentación: ${v.presentation?.name || "-"} | ` +
                              `Lote: ${v.batch_number} | ` +
                              `Marca: ${v.brand?.name || "-"} | ` +
                              `Vence: ${v.expiration_date} | ` +
                              `Stock: ${
                                v.inventories?.[0]?.current_quantity || 0
                              }`,
                          }))
                          .find((x) => x.value === currentVariant[detail.id]) ||
                        null
                      }
                      onChange={(option) =>
                        setCurrentVariant((prev) => ({
                          ...prev,
                          [detail.id]: option?.value || null,
                        }))
                      }
                      placeholder="Seleccione un lote..."
                    />
                  </div>

                  {/* CANTIDAD */}

                  <div className="col-span-12 lg:col-span-2">
                    <label className="font-semibold block mb-2">Cantidad</label>

                    <input
                      type="number"
                      min="0"
                      value={currentQuantity[detail.id] ?? ""}
                      onChange={(e) =>
                        setCurrentQuantity((prev) => ({
                          ...prev,
                          [detail.id]: e.target.value,
                        }))
                      }
                      className="border rounded px-3 py-2 w-full"
                      placeholder="Cantidad"
                    />
                  </div>

                  {/* MOTIVO */}

                  <div className="col-span-12 lg:col-span-2">
                    <label className="font-semibold block mb-2">Motivo</label>

                    <Select
                      options={deliveryReasonOptions}
                      placeholder="Seleccione..."
                      value={
                        deliveryReasonOptions.find(
                          (option) => option.value === deliveryReasons[detail.id],
                        ) || null
                      }
                      onChange={(option) => {
                        const reason = option?.value || null;
                        setDeliveryReasons((prev) => {
                          const next = { ...prev };
                          if (reason) next[detail.id] = reason;
                          else delete next[detail.id];
                          return next;
                        });
                        if (reason === "No hay disponibilidad") {
                          setDelivery((prev) => ({ ...prev, [detail.id]: [] }));
                          setCurrentVariant((prev) => ({ ...prev, [detail.id]: null }));
                          setCurrentQuantity((prev) => ({ ...prev, [detail.id]: "" }));
                        }
                      }}
                      isClearable
                    />
                  </div>

                  {/* AGREGAR */}

                  <div className="col-span-12 lg:col-span-2">
                    <label className="font-semibold block mb-2 invisible">
                      Acción
                    </label>

                    <button
                      onClick={() => addVariant(detail)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
                    >
                      <Plus size={18} />
                      Agregar
                    </button>
                  </div>
                </div>

                {/* TABLA DE LOTES */}

                {/* TABLA DE LOTES */}

                {delivery[detail.id]?.length > 0 && (
                  <table className="w-full mt-6 border">
                    <thead>
                      <tr className="bg-gray-100 text-center">
                        <th className="p-3">Lote</th>
                        <th className="p-3">INVIMA</th>
                        <th className="p-3">Marca</th>
                        <th className="p-3">Vence</th>
                        <th className="p-3">Cantidad</th>
                        <th className="p-3">Acción</th>
                      </tr>
                    </thead>

                    <tbody>
                      {delivery[detail.id].map((item, index) => (
                        <tr key={index} className="border-t text-center">
                          {/* LOTE */}
                          <td className="p-2">
                            {item.variant_id
                              ? item.batch_number
                              : "Sin disponibilidad"}
                          </td>

                          {/* INVIMA */}
                          <td className="p-2">
                            {item.variant_id ? item.invima_code : "—"}
                          </td>

                          {/* MARCA */}
                          <td className="p-2">
                            {item.variant_id ? item.brand : "—"}
                          </td>

                          {/* VENCIMIENTO */}
                          <td className="p-2">
                            {item.variant_id ? item.expiration_date : "—"}
                          </td>

                          {/* CANTIDAD */}
                          <td
                            className={`p-2 font-semibold ${
                              item.quantity === 0
                                ? "text-red-600"
                                : "text-green-600"
                            }`}
                          >
                            {item.quantity}
                          </td>

                          {/* ACCIÓN */}
                          <td className="p-2">
                            <button
                              onClick={() => removeVariant(detail.id, index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={18} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}

          <div className="border rounded-xl p-5 mb-6">
            <h3 className="text-xl font-bold mb-4">INSUMOS ADICIONALES</h3>
            <p className="text-gray-600 mb-4">
              Registre aquí un insumo distinto que también se entregará al área.
            </p>

            <div className="grid grid-cols-12 gap-4 items-end">
              <div className="col-span-12 lg:col-span-4">
                <label className="font-semibold block mb-2">Insumo</label>
                <Select
                  options={products.map((product) => ({ value: product.id, label: product.name }))}
                  value={products
                    .map((product) => ({ value: product.id, label: product.name }))
                    .find((option) => Number(option.value) === Number(additionalProduct)) || null}
                  onChange={(option) => {
                    setAdditionalProduct(option?.value || null);
                    setAdditionalVariant(null);
                    loadAdditionalVariants(option?.value);
                  }}
                  placeholder="Seleccione un insumo..."
                />
              </div>

              <div className="col-span-12 lg:col-span-5">
                <label className="font-semibold block mb-2">Variante / Lote</label>
                <Select
                  options={additionalVariants.map((variant) => ({
                    value: variant.id,
                    label: `Presentación: ${variant.presentation?.name || "-"} | Lote: ${variant.batch_number} | Stock: ${variant.inventories?.[0]?.current_quantity || 0}`,
                  }))}
                  value={additionalVariants
                    .map((variant) => ({ value: variant.id, label: `Presentación: ${variant.presentation?.name || "-"} | Lote: ${variant.batch_number} | Stock: ${variant.inventories?.[0]?.current_quantity || 0}` }))
                    .find((option) => Number(option.value) === Number(additionalVariant)) || null}
                  onChange={(option) => setAdditionalVariant(option?.value || null)}
                  placeholder="Seleccione una variante..."
                  isDisabled={!additionalProduct}
                />
              </div>

              <div className="col-span-8 lg:col-span-2">
                <label className="font-semibold block mb-2">Cantidad</label>
                <input
                  type="number"
                  min="1"
                  value={additionalQuantity}
                  onChange={(event) => setAdditionalQuantity(event.target.value)}
                  className="border rounded px-3 py-2 w-full"
                />
              </div>

              <div className="col-span-4 lg:col-span-1">
                <button
                  type="button"
                  onClick={addAdditionalVariant}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg flex justify-center"
                  title="Agregar insumo adicional"
                >
                  <Plus size={18} />
                </button>
              </div>
            </div>

            {additionalDelivery.length > 0 && (
              <div className="overflow-x-auto mt-6">
                <table className="w-full border text-sm">
                  <thead>
                    <tr className="bg-gray-100 text-center">
                      <th className="p-3">Insumo</th>
                      <th className="p-3">Lote</th>
                      <th className="p-3">INVIMA</th>
                      <th className="p-3">Marca</th>
                      <th className="p-3">Cantidad</th>
                      <th className="p-3">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {additionalDelivery.map((item, index) => (
                      <tr key={`${item.variant_id}-${index}`} className="border-t text-center">
                        <td className="p-2">{item.product_name}</td>
                        <td className="p-2">{item.batch_number}</td>
                        <td className="p-2">{item.invima_code}</td>
                        <td className="p-2">{item.brand}</td>
                        <td className="p-2 font-semibold text-green-600">{item.quantity}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => setAdditionalDelivery((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                            className="text-red-600 hover:text-red-700"
                            title="Eliminar insumo adicional"
                          >
                            <Trash2 size={18} className="mx-auto" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* GUARDAR */}

          <div className="flex justify-end mt-8">
            <button
              type="button"
              disabled={Object.keys(delivery).length === 0 && Object.keys(deliveryReasons).length === 0 && additionalDelivery.length === 0}
              onClick={() => setShowConfirmModal(true)}
              className={`px-6 py-3 rounded-lg flex items-center gap-2 text-white ${
                Object.keys(delivery).length === 0 && Object.keys(deliveryReasons).length === 0 && additionalDelivery.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700"
              }`}
            >
              <Save size={18} />
              Guardar entrega
            </button>
          </div>
        </>
      )}

      {/* =====================================================
          MODAL DE CONFIRMACIÓN Y FIRMAS
          ===================================================== */}

      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 flex justify-center items-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl my-8">
            {/* HEADER */}

            <div className="border-b px-6 py-4">
              <h3 className="text-2xl font-bold text-gray-800">
                Confirmar entrega
              </h3>

              <p className="text-gray-500 mt-1">
                Complete las firmas para registrar oficialmente la entrega.
              </p>
            </div>

            <div className="p-6">
              {/* INFORMACIÓN */}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Solicitud
                  </label>

                  <input
                    type="text"
                    value={request?.request_number || ""}
                    readOnly
                    className="w-full mt-1 border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Área
                  </label>

                  <input
                    type="text"
                    value={request?.client?.name || ""}
                    readOnly
                    className="w-full mt-1 border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>

                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Fecha
                  </label>

                  <input
                    type="text"
                    value={new Date().toLocaleString("es-CO")}
                    readOnly
                    className="w-full mt-1 border rounded-lg px-3 py-2 bg-gray-100"
                  />
                </div>
              </div>

              {/* RESUMEN */}

              <div className="mb-8">
                <h4 className="font-bold text-lg mb-3">Resumen de entrega</h4>

                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-100 text-center">
                        <th className="p-3 text-left">Producto</th>

                        <th className="p-3">Solicitado</th>

                        <th className="p-3">Entregado</th>

                        <th className="p-3">Diferencia</th>

                        <th className="p-3">Motivo</th>
                      </tr>
                    </thead>

                    <tbody>
                      {request?.request_details?.map((detail) => {
                        const delivered = getTotalDelivered(detail.id);

                        const difference =
                          Number(detail.requested_quantity) - delivered;

                        return (
                          <tr key={detail.id} className="border-t text-center">
                            <td className="p-3 text-left font-medium">
                              {detail.product?.name}
                            </td>

                            <td className="p-3">{detail.requested_quantity}</td>

                            <td className="p-3 font-semibold text-blue-600">
                              {delivered}
                            </td>

                            <td
                              className={`p-3 font-semibold ${
                                difference > 0
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {difference}
                            </td>

                            <td className="p-3">
                              {difference > 0
                                ? deliveryReasonOptions.find(
                                    (option) =>
                                      option.value ===
                                      deliveryReasons[detail.id],
                                  )?.label || "Sin motivo"
                                : "Completo"}
                            </td>
                          </tr>
                        );
                      })}
                      {additionalDelivery.map((item, index) => (
                        <tr
                          key={`additional-summary-${item.variant_id}-${index}`}
                          className="border-t text-center bg-gray-50"
                        >
                          <td className="p-3 text-left font-medium">
                            {item.product_name}
                          </td>
                          <td className="p-3">0</td>
                          <td className="p-3 font-semibold text-gray-700">
                            {item.quantity}
                          </td>
                          <td className="p-3">0</td>
                          <td className="p-3">Insumo adicional</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NOMBRE Y FIRMAS */}

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre de quien recibe
                </label>

                <input
                  type="text"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  placeholder="Ingrese el nombre completo"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* BODEGUERO */}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h4 className="font-bold text-lg">Firma de quien entrega</h4>

                      <p className="text-sm text-gray-500">
                        Responsable de la entrega
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        clearSignature(
                          warehouseCanvasRef,
                          setWarehouseSignature,
                          setWarehouseSignatureDrawn,
                        )
                      }
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Limpiar
                    </button>
                  </div>

                  <canvas
                    ref={warehouseCanvasRef}
                    width={600}
                    height={220}
                    className="w-full border-2 border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
                    onPointerDown={(e) =>
                      startDrawing(
                        e,
                        warehouseCanvasRef,
                        setIsDrawingWarehouse,
                        setWarehouseSignatureDrawn,
                      )
                    }
                    onPointerMove={(e) =>
                      drawSignature(e, warehouseCanvasRef, isDrawingWarehouse, setWarehouseSignatureDrawn)
                    }
                    onPointerUp={() =>
                      stopDrawing(
                        warehouseCanvasRef,
                        setIsDrawingWarehouse,
                        setWarehouseSignature,
                      )
                    }
                    onPointerCancel={() =>
                      stopDrawing(
                        warehouseCanvasRef,
                        setIsDrawingWarehouse,
                        setWarehouseSignature,
                      )
                    }
                  />

                  <div className="border-t mt-2 pt-2 text-center text-sm text-gray-500">
                    Firma del responsable de bodega
                  </div>
                </div>

                {/* ÁREA */}

                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h4 className="font-bold text-lg">
                        Firma de quien recibe
                      </h4>

                      <p className="text-sm text-gray-500">
                        Persona del área que recibe los insumos
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() =>
                        clearSignature(clientCanvasRef, setClientSignature, setClientSignatureDrawn)
                      }
                      className="text-sm text-red-600 hover:text-red-700"
                    >
                      Limpiar
                    </button>
                  </div>

                  <canvas
                    ref={clientCanvasRef}
                    width={600}
                    height={220}
                    className="w-full border-2 border-gray-300 rounded-lg bg-white cursor-crosshair touch-none"
                    onPointerDown={(e) =>
                      startDrawing(
                        e,
                        clientCanvasRef,
                        setIsDrawingClient,
                        setClientSignatureDrawn,
                      )
                    }
                    onPointerMove={(e) =>
                      drawSignature(e, clientCanvasRef, isDrawingClient, setClientSignatureDrawn)
                    }
                    onPointerUp={() =>
                      stopDrawing(
                        clientCanvasRef,
                        setIsDrawingClient,
                        setClientSignature,
                      )
                    }
                    onPointerCancel={() =>
                      stopDrawing(
                        clientCanvasRef,
                        setIsDrawingClient,
                        setClientSignature,
                      )
                    }
                  />

                  <div className="border-t mt-2 pt-2 text-center text-sm text-gray-500">
                    Firma de quien recibe
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}

            <div className="border-t px-6 py-4 flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="px-6 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
              >
                Cancelar
              </button>

              <button
                onClick={saveDelivery}
                disabled={
                  !warehouseSignatureDrawn ||
                  !clientSignatureDrawn ||
                  !receiverName.trim()
                }
                title={
                  !warehouseSignatureDrawn
                    ? "Falta la firma de quien entrega"
                    : !clientSignatureDrawn
                    ? "Falta la firma de quien recibe"
                    : !receiverName.trim()
                    ? "Falta el nombre de quien recibe"
                    : "Registrar entrega"
                }
                className={`px-6 py-2 rounded-lg text-white font-semibold transition-all ${
                  !warehouseSignatureDrawn ||
                  !clientSignatureDrawn ||
                  !receiverName.trim()
                    ? "bg-gray-400 cursor-not-allowed opacity-60"
                    : "bg-green-600 hover:bg-green-700 cursor-pointer"
                }`}
              >
                Confirmar y registrar entrega
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestDelivery;
